import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../services/authService";
import gradeService from "../services/gradeService";
import { cache, cacheKeys } from "../utils/cacheUtils";
import { filterByScope } from "../utils/professionScope";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recentActivityLoading, setRecentActivityLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    totalClasses: 0,
    totalSemesters: 0,
    overallAverage: 0,
    averagePerSemester: [],
    recentActivity: [],
    pendingSemesterReviews: 0,
  });

  const fetchAdminDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setRecentActivityLoading(true);

      // Fetch base data with caching (5 min TTL)
      let users, classes, semesters;

      const cachedUsers = cache.get(cacheKeys.USERS);
      const cachedClasses = cache.get(cacheKeys.CLASSES);
      const cachedSemesters = cache.get(cacheKeys.SEMESTERS);

      if (cachedUsers && cachedClasses && cachedSemesters) {
        users = cachedUsers;
        classes = cachedClasses;
        semesters = cachedSemesters;
      } else {
        const [usersResponse, classesResponse, semestersResponse] =
          await Promise.all([
            api.get("/users"),
            api.get("/classes"),
            api.get("/semesters"),
          ]);

        users = usersResponse.data || [];
        classes = classesResponse.data || [];
        semesters = semestersResponse.data || [];

        // Cache base data (5 min TTL)
        cache.set(cacheKeys.USERS, users, 5 * 60 * 1000);
        cache.set(cacheKeys.CLASSES, classes, 5 * 60 * 1000);
        cache.set(cacheKeys.SEMESTERS, semesters, 5 * 60 * 1000);
      }

      let students = users.filter((u) =>
        u.roles?.some((r) => r.name === "LERNENDE"),
      );

      // Restrict to the professions the current user is responsible for
      // (admins are unrestricted; instructors may hold several).
      students = filterByScope(user, students);

      // Set basic stats immediately - FAST!
      setDashboardData((prev) => ({
        ...prev,
        totalStudents: students.length,
        totalClasses: classes.length,
        totalSemesters: semesters.length,
      }));
      setLoading(false);

      // Load grades and calculate averages in background
      const gradesArrays = await Promise.all(
        students.map(async (student) => {
          const cacheKey = cacheKeys.STUDENT_GRADES(student.id);
          const cachedGrades = cache.get(cacheKey);

          if (cachedGrades) {
            return { studentId: student.id, grades: cachedGrades };
          }

          try {
            const grades = await gradeService.getGradesByUser(student.id);
            cache.set(cacheKey, grades, 3 * 60 * 1000);
            return { studentId: student.id, grades };
          } catch (error) {
            return { studentId: student.id, grades: [] };
          }
        }),
      );

      const allGrades = gradesArrays.flatMap((item) => item.grades);

      // Calculate overall average
      const validGrades = allGrades.filter(
        (g) => g.gradeValue && g.gradeValue > 0,
      );
      const overallAverage =
        validGrades.length > 0
          ? (
              validGrades.reduce((sum, g) => sum + g.gradeValue, 0) /
              validGrades.length
            ).toFixed(2)
          : 0;

      // Calculate average per semester
      const semesterAverages = semesters
        .map((semester) => {
          const semesterGrades = validGrades.filter(
            (g) => g.semesterId === semester.id,
          );
          const average =
            semesterGrades.length > 0
              ? (
                  semesterGrades.reduce((sum, g) => sum + g.gradeValue, 0) /
                  semesterGrades.length
                ).toFixed(2)
              : 0;

          return {
            name: semester.name || `Semester ${semester.id}`,
            average: parseFloat(average),
            gradeCount: semesterGrades.length,
            semesterId: semester.id,
          };
        })
        .filter((s) => s.gradeCount > 0);

      // Update with averages
      setDashboardData((prev) => ({
        ...prev,
        overallAverage: parseFloat(overallAverage),
        averagePerSemester: semesterAverages,
      }));

      // Calculate recent activity - LAST (can be slow)
      // Build a map for faster student lookup
      const studentMap = new Map(students.map((s) => [s.id, s]));

      const recentActivity = allGrades
        .filter((g) => g.examDate)
        .sort((a, b) => new Date(b.examDate) - new Date(a.examDate))
        .slice(0, 10)
        .map((grade) => {
          const student = studentMap.get(grade.userId);
          return {
            id: grade.id,
            studentName: student
              ? `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
                student.username
              : "Unknown Student",
            gradeValue: grade.gradeValue,
            examDate: grade.examDate,
          };
        });

      setDashboardData((prev) => ({
        ...prev,
        recentActivity: recentActivity,
      }));
      setRecentActivityLoading(false);
    } catch (error) {
      console.error("❌ Error fetching admin dashboard data:", error);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchAdminDashboardData();
    }
  }, [user, fetchAdminDashboardData]);

  const getAverageColor = (average) => {
    if (average >= 5.0) return "text-green-cloud";
    if (average >= 4.0) return "text-slate-grey";
    return "text-sunrise-red";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="h-8 bg-slate-cloud/20 rounded w-64 mb-2 animate-pulse"></div>
              <div className="h-4 bg-slate-cloud/20 rounded w-96 animate-pulse"></div>
            </div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="card-brand-elevated p-6">
                <div className="h-4 bg-slate-cloud/20 rounded w-24 mb-4 animate-pulse"></div>
                <div className="h-8 bg-slate-cloud/20 rounded w-16 mb-2 animate-pulse"></div>
                <div className="h-3 bg-slate-cloud/20 rounded w-20 animate-pulse"></div>
              </div>
            ))}
          </div>

          {/* Navigation Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-brand-elevated p-6 h-48">
                <div className="h-6 bg-slate-cloud/20 rounded w-32 mb-3 animate-pulse"></div>
                <div className="h-4 bg-slate-cloud/20 rounded w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-slate-cloud/20 rounded w-3/4 animate-pulse"></div>
              </div>
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {[1, 2].map((i) => (
              <div key={i} className="card-brand-elevated p-6">
                <div className="h-6 bg-slate-cloud/20 rounded w-48 mb-6 animate-pulse"></div>
                <div className="h-64 bg-slate-cloud/10 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk">
                  Admin Dashboard
                </h1>
                <p className="text-slate-cloud mt-2">
                  Welcome back, {user?.firstName}! Here's an overview of your
                  system.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 auto-rows-fr">
          {/* Apprentice Directory */}
          <Link to="/students" className="block group h-full">
            <div className="bg-ice-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow duration-200 border-2 border-transparent hover:border-sunrise-red h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-sunrise-red/10 rounded-full p-3">
                  <svg
                    className="w-8 h-8 text-sunrise-red"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <svg
                  className="w-6 h-6 text-slate-cloud group-hover:text-sunrise-red transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-grey-dusk mb-2">
                Apprentice Directory
              </h3>
              <p className="text-slate-cloud text-sm">
                Manage apprentices, view profiles, and progress
              </p>
              <div className="mt-4 text-sunrise-red font-semibold text-sm group-hover:underline">
                View Apprentices →
              </div>
            </div>
          </Link>

          {/* Class Management */}
          <Link to="/management" className="block group h-full">
            <div className="bg-ice-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow duration-200 border-2 border-transparent hover:border-green-cloud h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-cloud/10 rounded-full p-3">
                  <svg
                    className="w-8 h-8 text-green-cloud"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />{" "}
                  </svg>
                </div>
                <svg
                  className="w-6 h-6 text-slate-cloud group-hover:text-green-cloud transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-grey-dusk mb-2">
                Management
              </h3>
              <p className="text-slate-cloud text-sm">
                Create and manage classes, schools and subjects
              </p>
              <div className="mt-4 text-green-cloud font-semibold text-sm group-hover:underline">
                Manage →
              </div>
            </div>
          </Link>

          {/* Semester Reviews */}
          <Link to="/trainer/semester-reviews" className="block group">
            <div className="bg-ice-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow duration-200 border-2 border-transparent hover:border-slate-cloud">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-slate-cloud/10 rounded-full p-3">
                  <svg
                    className="w-8 h-8 text-slate-cloud"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <svg
                  className="w-6 h-6 text-slate-cloud group-hover:text-slate-cloud transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-grey-dusk mb-2">
                Semester Reviews
              </h3>
              <p className="text-slate-cloud text-sm">
                Conduct and manage semester reviews
              </p>
              <div className="mt-4 text-slate-cloud font-semibold text-sm group-hover:underline">
                View Reviews →
              </div>
            </div>
          </Link>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Students */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-sunrise-red">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Total Students
                </p>
                <p className="text-3xl font-bold text-grey-dusk mt-2">
                  {dashboardData.totalStudents}
                </p>
              </div>
              <div className="bg-sunrise-red/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-sunrise-red"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Overall Average */}
          <div
            className={`bg-ice-white rounded-xl shadow-md p-6 border-l-4 ${
              dashboardData.overallAverage >= 4.0
                ? "border-green-cloud"
                : "border-warm-sunset"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Overall Average
                </p>
                <p
                  className={`text-3xl font-bold mt-2 ${getAverageColor(
                    dashboardData.overallAverage,
                  )}`}
                >
                  {dashboardData.overallAverage > 0
                    ? dashboardData.overallAverage
                    : "—"}
                </p>
              </div>
              <div
                className={`${
                  dashboardData.overallAverage >= 4.0
                    ? "bg-green-cloud/10"
                    : "bg-warm-sunset/10"
                } rounded-full p-3`}
              >
                <svg
                  className={`w-8 h-8 ${
                    dashboardData.overallAverage >= 4.0
                      ? "text-green-cloud"
                      : "text-warm-sunset"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Today's Date */}
          <div className="bg-ice-white rounded-xl shadow-md p-6 border-l-4 border-slate-cloud">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-cloud text-sm font-medium">
                  Today's Date
                </p>
                <p className="text-2xl font-bold text-grey-dusk mt-2">
                  {new Date().toLocaleDateString("de-CH", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div className="bg-slate-cloud/10 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-slate-cloud"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Grade Activity */}
        <div className="bg-ice-white rounded-xl shadow-md mt-8">
          <div className="px-6 py-4 border-b border-pale-dawn">
            <h2 className="text-xl font-bold text-grey-dusk">
              Recent Grade Activity
            </h2>
            <p className="text-sm text-slate-cloud mt-1">
              Most recent grades across all students
            </p>
          </div>

          {recentActivityLoading ? (
            <div className="p-6 space-y-3 animate-pulse">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="flex-1 h-10 bg-slate-cloud/10 rounded"></div>
                  <div className="flex-1 h-10 bg-slate-cloud/10 rounded"></div>
                  <div className="w-16 h-10 bg-slate-cloud/10 rounded"></div>
                  <div className="w-24 h-10 bg-slate-cloud/10 rounded"></div>
                </div>
              ))}
            </div>
          ) : dashboardData.recentActivity &&
            dashboardData.recentActivity.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-pale-dawn">
                <thead className="bg-pale-dawn/30">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-ice-white divide-y divide-pale-dawn">
                  {dashboardData.recentActivity.map((activity) => (
                    <tr
                      key={activity.id}
                      className="hover:bg-pale-dawn/20 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-grey-dusk">
                        {activity.studentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            activity.gradeValue >= 4
                              ? "bg-green-cloud/20 text-green-cloud border border-green-cloud/30"
                              : "bg-sunrise-red/20 text-sunrise-red border border-sunrise-red/30"
                          }`}
                        >
                          {activity.gradeValue.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-cloud">
                        {new Date(activity.examDate).toLocaleDateString(
                          "de-CH",
                          { day: "2-digit", month: "2-digit", year: "numeric" },
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-cloud/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-chart-line text-slate-cloud text-xl"></i>
              </div>
              <p className="text-slate-cloud">No recent grade activity found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
